import React, { useContext, useEffect, useMemo, useState } from 'react';
import { UserPolicy } from './types/UserPolicy';
import { hasSystemPolicy, hasTeamPolicy } from '../utility';
import { TeamMemberPolicy } from './types/TeamMemberPolicy';
import { useTranslation } from 'react-i18next';
import { AuthenticationContext } from './AuthenticationProvider';
import {
  getTeam,
  getUsersIndex,
  createTeamMember,
  updateTeamMember,
  deleteTeamMember,
} from './api';
import { TeamResponseTeamMember } from './types/TeamResponse';
import { User } from './types/User';
import { trackPromise } from 'react-promise-tracker';
export type TeamMemberData = {
  id?: number;
  userId?: number;
  firstName: string;
  lastName: string;
  email: string;
  systemAdmin: boolean;
  teamAdmin: boolean;
  teamMemberAdmin: boolean;
  configAdmin: boolean;
  canCheckOut: boolean;
  canCreate: boolean;
  canDelete: boolean;
};

export type CreateTeamMember = {
  team_member: {
    id?: number;
    team_id: number;
    user_id: number;
    team_admin: boolean;
    team_member_admin: boolean;
    config_admin: boolean;
    can_check_out: boolean;
    can_create: boolean;
    can_delete: boolean;
  };
};

export type AvailableUser = {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
};

export interface ITeamMembersContext {
  teamMembers: TeamMemberData[];
  availableUsers: AvailableUser[];
  teamId: number | undefined;

  isTeamAdmin: boolean;

  onCreateTeamMember(_values: TeamMemberData): void;
  onUpdateTeamMember(_valued: TeamMemberData): void;
  onDeleteTeamMember(_id: number): void;
}

export interface ITeamMembersProviderProps {
  projectId: number;
  children: any;
}

const TeamMembersContext = React.createContext<ITeamMembersContext>({
  teamMembers: [],
  availableUsers: [],
  teamId: undefined,

  isTeamAdmin: false,

  onCreateTeamMember(_values: TeamMemberData) {},
  onUpdateTeamMember(_valued: TeamMemberData) {},
  onDeleteTeamMember(_id: number) {},
});

const createTeamMemberData = (
  teamMembers: TeamResponseTeamMember[],
  teamId: number
) => {
  let arr: TeamMemberData[] = [];
  teamMembers.forEach((member) => {
    arr.push({
      id: member.id,
      userId: member.user.id,
      firstName: member.user.first_name,
      lastName: member.user.last_name,
      email: member.user.email,
      systemAdmin: hasSystemPolicy(
        'FCC_Admin',
        member.user.user_policies as UserPolicy[]
      ),
      teamAdmin: hasTeamPolicy(
        'FCC_TeamAdmin',
        teamId,
        member.team_member_policies as TeamMemberPolicy[]
      ),
      teamMemberAdmin: hasTeamPolicy(
        'FCC_CanManageTeamMembers',
        teamId,
        member.team_member_policies as TeamMemberPolicy[]
      ),
      configAdmin: hasTeamPolicy(
        'FCC_CanManageConfigs',
        teamId,
        member.team_member_policies as TeamMemberPolicy[]
      ),
      canCheckOut: hasTeamPolicy(
        'FCC_CanCheckoutResources',
        teamId,
        member.team_member_policies as TeamMemberPolicy[]
      ),
      canCreate: hasTeamPolicy(
        'FCC_CanCreateResources',
        teamId,
        member.team_member_policies as TeamMemberPolicy[]
      ),
      canDelete: hasTeamPolicy(
        'FCC_CanDeleteResources',
        teamId,
        member.team_member_policies as TeamMemberPolicy[]
      ),
    });
  });

  return arr;
};

const createAvailableUsers = (
  users: User[],
  teamMembers: TeamResponseTeamMember[]
) => {
  let arr: AvailableUser[] = [];

  users.forEach((user) => {
    const found = teamMembers.find((m) => m.user.id === user.id);

    if (!found) {
      arr.push({
        id: user.id,
        firstName: user.first_name,
        lastName: user.last_name,
        email: user.email,
      });
    }
  });

  return arr;
};

const TeamMembersProvider = (props: ITeamMembersProviderProps) => {
  const { children, projectId } = props;

  const [teamMembers, setTeamMembers] = useState<TeamMemberData[]>([]);
  const [availableUsers, setAvailableUsers] = useState<AvailableUser[]>([]);
  const [teamId, setTeamId] = useState<number | undefined>();

  const { i18n } = useTranslation();

  const { email, isAuthenticated, token } = useContext(AuthenticationContext);

  useEffect(() => {
    if (isAuthenticated() && token && projectId) {
      trackPromise(getTeam(projectId, token))
        .then((teamData) => {
          if (teamData) {
            if (teamData.team.team_members.length > 0) {
              setTeamId(teamData.team.team_members[0].team_id);
              setTeamMembers(
                createTeamMemberData(
                  teamData.team.team_members,
                  teamData.team.team_members[0].team_id
                )
              );
            }
            trackPromise(getUsersIndex(token)).then((data) => {
              if (data) {
                setAvailableUsers(
                  createAvailableUsers(data.users, teamData.team.team_members)
                );
              }
            });
          }
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }, [isAuthenticated, i18n, token, projectId]);

  const getTeamIndex = (projectId: number, token: string) =>
    trackPromise(getTeam(projectId, token))
      .then((teamData) => {
        if (teamData) {
          if (teamData.team.team_members.length > 0) {
            setTeamId(teamData.team.team_members[0].team_id);
            setTeamMembers(
              createTeamMemberData(
                teamData.team.team_members,
                teamData.team.team_members[0].team_id
              )
            );
          }
          getUsersIndex(token).then((data) => {
            if (data) {
              setAvailableUsers(
                createAvailableUsers(data.users, teamData.team.team_members)
              );
            }
          });
        }
      })
      .catch((err) => {
        console.log(err);
      });

  const onCreateTeamMember = (values: TeamMemberData) => {
    if (isAuthenticated() && token) {
      trackPromise(
        createTeamMember(
          {
            team_member: {
              team_id: teamId as number,
              user_id: values.userId as number,
              team_admin: values.teamAdmin,
              team_member_admin: values.teamMemberAdmin,
              config_admin: values.configAdmin,
              can_check_out: values.canCheckOut,
              can_create: values.canCreate,
              can_delete: values.canDelete,
            },
          },
          token as string
        )
      )
        .then(() => {
          trackPromise(getTeamIndex(projectId, token))
            .then()
            .catch((err) => console.log(err));
        })
        .catch((err) => console.log(err));
    }
  };

  const onUpdateTeamMember = (values: TeamMemberData) => {
    if (isAuthenticated() && token) {
      trackPromise(
        updateTeamMember(
          {
            team_member: {
              id: values.id as number,
              team_id: teamId as number,
              user_id: values.userId as number,
              team_admin: values.teamAdmin,
              team_member_admin: values.teamMemberAdmin,
              config_admin: values.configAdmin,
              can_check_out: values.canCheckOut,
              can_create: values.canCreate,
              can_delete: values.canDelete,
            },
          },
          token as string
        )
      )
        .then(() => {
          trackPromise(getTeamIndex(projectId, token))
            .then()
            .catch((err) => console.log(err));
        })
        .catch((err) => console.log(err));
    }
  };

  const onDeleteTeamMember = (id: number) => {
    if (isAuthenticated() && token) {
      trackPromise(deleteTeamMember(id, token))
        .then(() => {
          getTeamIndex(projectId, token)
            .then()
            .catch((err) => console.log(err));
        })
        .catch((err) => {
          console.log(err);
        });
    }
  };

  const isTeamAdmin = useMemo(() => {
    const currentUser = teamMembers.find((m) => m.email === email);
    return !!currentUser && currentUser.teamAdmin;
  }, [email, teamMembers])

  return (
    <TeamMembersContext.Provider
      value={{
        teamMembers,
        availableUsers,
        teamId,

        isTeamAdmin,

        onCreateTeamMember,
        onUpdateTeamMember,
        onDeleteTeamMember,
      }}
    >
      {children}
    </TeamMembersContext.Provider>
  );
};

export { TeamMembersContext, TeamMembersProvider };
